import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BodyText, TextLink, TextSpace, theme } from '@sg/garnish';

import { HorizontalSafeAreaContainer } from '../../../components';
import { useCustomerData } from '../../../context/Customer/hooks';
import {
  useLastInteractedStore,
  useNavigateToLastInteractedStore,
} from '../../../context/LastInteractedStore';

export const OrdersHeader = (props: OrdersHeaderProps) => {
  const { isReorderEnabled } = props;

  const { formatMessage } = useIntl();

  const { customer } = useCustomerData();
  const lastInteractedStore = useLastInteractedStore();

  const { addressId, restaurantName } = lastInteractedStore ?? {};

  const addressName = customer?.addresses?.find((address) => address.id === addressId)?.name;

  const location = addressName ?? restaurantName;

  const router = useRouter();
  const navigateToLastInteractedStore = useNavigateToLastInteractedStore();

  const shouldRenderReorderTitle = isReorderEnabled && location;

  return (
    <HorizontalSafeAreaContainer>
      <SafeAreaView edges={['top']} style={styles.header}>
        {shouldRenderReorderTitle ? (
          <View style={styles.titleContainer}>
            <BodyText numberOfLines={1} sizeMatch={['22']} style={styles.title}>
              <FormattedMessage {...messages.title} />

              <TextSpace />

              <TextLink
                sizeMatch={['22']}
                accessibilityRole="link"
                accessibilityLabel={formatMessage(messages.accessibilityLabel)}
                style={styles.link}
                onPress={navigateToLastInteractedStore}
              >
                {location}
              </TextLink>
            </BodyText>
          </View>
        ) : null}
      </SafeAreaView>
    </HorizontalSafeAreaContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
    minHeight: 64,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    textAlign: 'center',
  },
  link: {
    color: theme.colors.KALE,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Reorder to',
    description: 'Account | Orders | Reorder Title | Title',
  },
  accessibilityLabel: {
    defaultMessage: 'Opens menu for location',
    description: 'Account | Orders | Reorder Title | Accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrdersHeaderProps = Readonly<{
  isReorderEnabled: boolean;
}>;
